/** Your chat personality: four reels spin and land on your traits, then the type they add up to. */

import { useLayoutEffect, useRef, useState } from "react";

import { TRAIT_LABELS } from "../../copy";
import { Fit, Letters, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { tokenValue } from "../../theme";
import type { Personality as PersonalityType, Traits } from "../../types";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1010, y: 1800, r: 260, seed: 13.6, wob: 0.2 },
  { colour: "blurple", x: 40, y: 1640, r: 120, seed: 14.1, wob: 0.24 },
];

type Reel<K extends keyof Traits = keyof Traits> = { key: K; label: string; a: Traits[K]; b: Traits[K] };

const REELS: Reel[] = [
  { key: "time", label: "WHEN", a: "day", b: "night" },
  { key: "role", label: "ROLE", a: "replier", b: "starter" },
  { key: "length", label: "LENGTH", a: "long", b: "short" },
  { key: "medium", label: "MOSTLY", a: "words", b: "memes" },
];

const SLOT = 108;
const NAME_SIZE = 170;
// From the name's top to just above "1 OF 16 CHAT TYPES": two lines at full size.
const NAME_ROOM = 310;

export function Personality({ personality }: { personality: PersonalityType }) {
  const { traits, reasons, type } = personality;
  const name = useRef<HTMLDivElement>(null);
  const [nameSize, setNameSize] = useState(NAME_SIZE);

  // Some type names would wrap to three lines at full size; shrink until they take two.
  useLayoutEffect(() => {
    const el = name.current;
    if (!el) return;
    let next = NAME_SIZE;
    el.style.fontSize = `${next}px`;
    // jsdom has no layout: heights read 0 there, so the size stays as given.
    while (next > 80 && el.offsetHeight > NAME_ROOM) {
      next = Math.floor(next * 0.94);
      el.style.fontSize = `${next}px`;
    }
    setNameSize(next);
  }, [type]);

  const root = useTimeline((s) => {
    s.rise("[data-part=kick]", 0.35, { y: 0 });
    const ink = tokenValue("ink");
    const yellow = tokenValue("yellow");
    const why = s.q(".trait");
    s.q(".reel-row").forEach((row, i) => {
      s.tl.fromTo(row, { autoAlpha: 0, x: -80 }, { autoAlpha: 1, x: 0, duration: 0.45 }, 0.4 + i * 0.08);
      // Each reel spins a little longer than the one above it.
      const stop = 1.0 + i * 0.35;
      const strip = row.querySelector<HTMLElement>(".strip");
      s.tl.fromTo(strip, { y: 0 }, { y: -Number(strip?.dataset.steps) * SLOT, duration: stop, ease: "back.out(0.9)" }, 0.6);
      s.tl.fromTo(row.querySelector(".reel"), { borderColor: ink }, { borderColor: yellow, duration: 0.12, ease: "none" }, 0.55 + stop);
      s.sound("chunk", 0.55 + stop, 1 + i * 0.08);
      s.rise(why[i], 0.6 + stop, { y: 16, dur: 0.4, sound: false });
    });
    // Slot-machine clicks, fast at first and slowing as the last reel comes to rest.
    s.ticksAlong(0.6, 1.0 + 3 * 0.35, "power2.out", 22);
    s.rise("[data-part=you]", 3.0, { y: 0, sound: false });
    s.letters(".t-name", 3.1, { y: 110, stagger: 0.03 });
    s.rise("[data-part=note]", 4.3, { y: 0, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            Your chat personality
          </div>
        </Zone>
        {/* The reels spin in the middle; the type they land on reads as the foot. */}
        <Zone at="middle">
          {REELS.map((reel, i) => {
            const top = 268 + i * 190;
            const trait = traits[reel.key];
            // The strip alternates the two options and ends on yours: odd steps land on the second.
            const steps = 15 + i * 4 + (trait === reel.b ? 0 : 1);
            return (
              <div key={reel.key}>
                <div className="reel-row" style={{ top }}>
                  <div className="mono">{reel.label}</div>
                  <span className="sr-only">{(TRAIT_LABELS[reel.key] as Record<string, string>)[trait]}</span>
                  <div className="reel" style={{ borderColor: "var(--wr-yellow)" }} aria-hidden>
                    <div className="strip" data-steps={steps} style={{ transform: `translateY(${-steps * SLOT}px)` }}>
                      {Array.from({ length: steps + 1 }, (_, k) => (
                        <span key={k}>{(k % 2 ? reel.b : reel.a).toUpperCase()}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <Fit size={30} min={20} className="trait" style={{ top: top + 130 }}>
                  {reasons[reel.key]}
                </Fit>
              </div>
            );
          })}
        </Zone>
        <Zone at="bottom">
          <div data-part="you" className="t-you">
            You are
          </div>
          <div ref={name} className="t-name disp" style={{ fontSize: nameSize }}>
            <span className="sr-only">{type}</span>
            <span aria-hidden>
              <Letters text={type} />
            </span>
          </div>
          <div data-part="note" className="t-note">
            1 OF 16 CHAT TYPES
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
